import { useCallback, useState } from "react";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { useReportViewer } from "../../../../config/hooks/utils/report_viewer";
import { ChecklistMaquinaEquipModel } from "../../domain/models/checklist_maquina_equip";
import { initialChecklistMaquinaEquipLista, useChecklistMaquinaEquipListaFilter } from "../hooks/ChecklistMaquinaEquip_lista/filters";
import { useChecklistMaquinaEquipListaPagination } from "../hooks/ChecklistMaquinaEquip_lista/pagination";
import { ChecklistMaquinaEquipForm } from "../schemas/ChecklistMaquinaEquip_lista/checklist_maquina_equip";

/**
 * LoadChecklistMaquinaEquipFuncState
 */

type LoadChecklistMaquinaEquipFuncState = {
  descricao?: string | null;
  codigo?: string | null;
  status?: number;

  page?: number;
  rowsPerPage?: number;
};

/**
 * useChecklistMaquinaEquipListaViewModel
 */

export const useChecklistMaquinaEquipListaViewModel = () => {
  const {
    controlePatrimonio: {
      getAllChecklistMaquinaEquipUseCase,
      getChecklistMaquinaEquipByIdUseCase,
      updateChecklistMaquinaEquipUseCase,
      createChecklistMaquinaEquipUseCase,
      deleteChecklistMaquinaEquipUseCase,
    },
  } = useDependencyInjection();

  const { pushErrorNotification, pushInfoNotification, setPageSaving, setCustomLoading } = usePageLoader();

  const backOrNavigate = useBackOrNavigate();

  const debouncedHandler = useCreateDebounceHandler(750);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [checklistMaquinaEquips, setChecklistMaquinaEquips] = useState<ChecklistMaquinaEquipModel[]>();

  const [selectedChecklistMaquinaEquip, setSelectedChecklistMaquinaEquip] = useState<ChecklistMaquinaEquipModel>();

  const [checklistMaquinaEquipModal, setChecklistMaquinaEquipModal] = useState(false);
  const [checklistMaquinaEquipDeleteModal, setChecklistMaquinaEquipDeleteModal] = useState(false);
  const [copyChecklistMaquinaEquipModal, setCopyChecklistMaquinaEquipModal] = useState(false);

  const filters = useChecklistMaquinaEquipListaFilter();
  const pagination = useChecklistMaquinaEquipListaPagination();

  const [reportViewerState, startReportViewerProcess] = useReportViewer();

  /**
   * Funcoes de carregamento
   */

  const loadChecklistMaquinaEquips = useCallback(async (fs?: LoadChecklistMaquinaEquipFuncState) => {
    try {
      const filteredCodigo = evaluateParamValue(fs?.codigo, filters.codigo);
      const filteredDescricao = evaluateParamValue(fs?.descricao, filters.descricao);
      const filteredStatus = evaluateParamValue(fs?.status, filters.status);

      const filteredPage = fs?.page ?? pagination.page;
      const filteredRowsPerPage = fs?.rowsPerPage ?? pagination.rowsPerPage;

      await getAllChecklistMaquinaEquipUseCase.execute(
        filteredStatus,
        filteredCodigo,
        null,
        filteredDescricao,
        filteredPage,
        filteredRowsPerPage
      );

      setChecklistMaquinaEquips(getAllChecklistMaquinaEquipUseCase.items);
      pagination.setTotalItems(getAllChecklistMaquinaEquipUseCase.totalItems);
    } catch (e: unknown) {
      throw new Error('Falha ao carregar dados da pagina!');
    }
  }, [
    getAllChecklistMaquinaEquipUseCase,
    filters,
    pagination,
  ]);

  /**
   * useEffect
   */

  usePageStartBootstrap(async () => {
    try {
      await loadChecklistMaquinaEquips();
    } catch (e: unknown) {
      setErrorMessage(processError(e, 'Falha ao buscar dados pagina!')[0]);
    }
  });

  /**
   * Eventos
   */

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);
      await loadChecklistMaquinaEquips();
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao carregar dados da pagina!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadChecklistMaquinaEquips,
    pushErrorNotification,
  ]);

  const handleChecklistMaquinaEquipSubmit = useCallback(async (form: ChecklistMaquinaEquipForm) => {
    try {
      setSaving(true);
      setPageSaving(true);

      if (selectedChecklistMaquinaEquip) {
        await updateChecklistMaquinaEquipUseCase.execute(
          selectedChecklistMaquinaEquip.idChecklistMaquinaEquip ?? 0,
          form.status === 1 ? true : false,
          form.codigo,
          form.nroRevisao,
          form.descricao,
        );
      } else {
        await createChecklistMaquinaEquipUseCase.execute(
          form.codigo,
          form.nroRevisao,
          form.descricao,
        );
      }

      await loadChecklistMaquinaEquips();

      setChecklistMaquinaEquipModal(false);
      setSelectedChecklistMaquinaEquip(undefined);
      pushInfoNotification('Sucesso', 'Registro salvo com sucesso!');
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao salvar checklist de serviço!"));
    } finally {
      setSaving(false);
      setPageSaving(false);
    }
  }, [
    selectedChecklistMaquinaEquip,
    createChecklistMaquinaEquipUseCase,
    updateChecklistMaquinaEquipUseCase,
    loadChecklistMaquinaEquips,
    pushErrorNotification,
    pushInfoNotification,
    setPageSaving,
  ]);

  const handleChecklistMaquinaEquipDeleteSubmit = useCallback(async () => {
    if (selectedChecklistMaquinaEquip) {
      try {
        setSaving(true);
        setPageSaving(true);

        await deleteChecklistMaquinaEquipUseCase.execute(
          selectedChecklistMaquinaEquip.idChecklistMaquinaEquip ?? 0,
        );

        await loadChecklistMaquinaEquips();

        setChecklistMaquinaEquipDeleteModal(false);
        setSelectedChecklistMaquinaEquip(undefined);

        pushInfoNotification('Sucesso', 'Registro excluido com sucesso!');
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao excluir checklist de serviço!"));
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [
    selectedChecklistMaquinaEquip,
    deleteChecklistMaquinaEquipUseCase,
    loadChecklistMaquinaEquips,
    pushErrorNotification,
    pushInfoNotification,
    setPageSaving,
  ]);

  // const handleCopyChecklistServicoSubmit = useCallback(async (form: CopyChecklistServicoForm) => {
  //   if (selectedChecklistServico) {
  //     try {
  //       setSaving(true);
  //       setPageSaving(true);

  //       await copyChecklistServicoUseCase.copyChecklistServico(
  //         selectedChecklistServico.idChecklistServico,
  //         form.codigo,
  //         form.nroRevisao,
  //         form.descricao,
  //       );

  //       await loadChecklistServicos();

  //       setCopyChecklistServicoModal(false);
  //       setSelectedChecklistServico(undefined);

  //       pushInfoNotification('Sucesso', 'Registro copiado com sucesso!');
  //     } catch (e: unknown) {
  //       pushErrorNotification("Erro", processError(e, "Falha ao copiar checklist de serviço!"));
  //     } finally {
  //       setSaving(false);
  //       setPageSaving(false);
  //     }
  //   }
  // }, [
  //   selectedChecklistServico,
  //   copyChecklistServicoUseCase,
  //   loadChecklistServicos,
  //   pushErrorNotification,
  //   pushInfoNotification,
  //   setPageSaving,
  // ]);

  const handleCodigoChange = useDebouncedCallback(debouncedHandler, (codigo?: string | null) => {
    useChecklistMaquinaEquipListaFilter.setState({ codigo });
    return async () => {
      try {
        setLoading(true);
        useChecklistMaquinaEquipListaPagination.setState({ page: 1 });
        await loadChecklistMaquinaEquips({ codigo, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao carregar dados da pagina!"));
      } finally {
        setLoading(false);
      }
    }
  }, [
    pushErrorNotification,
    loadChecklistMaquinaEquips,
  ]);

  const handleDescricaoChange = useDebouncedCallback(debouncedHandler, (descricao?: string | null) => {
    useChecklistMaquinaEquipListaFilter.setState({ descricao });
    return async () => {
      try {
        setLoading(true);
        useChecklistMaquinaEquipListaPagination.setState({ page: 1 });
        await loadChecklistMaquinaEquips({ descricao, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao carregar dados da pagina!"));
      } finally {
        setLoading(false);
      }
    }
  }, [
    pushErrorNotification,
    loadChecklistMaquinaEquips,
  ]);

  const handleStatusChange = useDebouncedCallback(debouncedHandler, (status?: number) => {
    useChecklistMaquinaEquipListaFilter.setState({ status });
    return async () => {
      try {
        setLoading(true);
        useChecklistMaquinaEquipListaPagination.setState({ page: 1 });
        await loadChecklistMaquinaEquips({ status, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao carregar dados da pagina!"));
      } finally {
        setLoading(false);
      }
    }
  }, [
    pushErrorNotification,
    loadChecklistMaquinaEquips,
  ]);

  const handleClearFilters = useCallback(async () => {
    try {
      setLoading(true);
      useChecklistMaquinaEquipListaFilter.setState({ ...initialChecklistMaquinaEquipLista })
      useChecklistMaquinaEquipListaPagination.setState({ page: 1 });
      await loadChecklistMaquinaEquips({ ...initialChecklistMaquinaEquipLista, page: 1 });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao carregar dados da pagina!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadChecklistMaquinaEquips,
    pushErrorNotification,
  ]);

  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      useChecklistMaquinaEquipListaPagination.setState({ page: page + 1 });
      await loadChecklistMaquinaEquips({ page: page + 1 });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao carregar dados da pagina!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadChecklistMaquinaEquips,
    pushErrorNotification,
  ]);

  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);
      useChecklistMaquinaEquipListaPagination.setState({ page: 1, rowsPerPage });
      await loadChecklistMaquinaEquips({ page: 1, rowsPerPage });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao carregar dados da pagina!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadChecklistMaquinaEquips,
    pushErrorNotification,
  ]);

  const handleChecklistMaquinaEquipModal = useCallback((idChecklistMaquinaEquip: number | null, openState: boolean) => {
    if (openState) {
      if (idChecklistMaquinaEquip && checklistMaquinaEquips) {
        setSelectedChecklistMaquinaEquip(checklistMaquinaEquips.find(x => x.idChecklistMaquinaEquip === idChecklistMaquinaEquip));
      } else {
        setSelectedChecklistMaquinaEquip(undefined);
      }
    }
    setChecklistMaquinaEquipModal(openState);
  }, [
    checklistMaquinaEquips,
  ]);

  const handleChecklistMaquinaEquipDeleteModal = useCallback((idChecklistMaquinaEquip: number | null, openState: boolean) => {
    if (openState) {
      if (idChecklistMaquinaEquip && checklistMaquinaEquips) {
        setSelectedChecklistMaquinaEquip(checklistMaquinaEquips.find(x => x.idChecklistMaquinaEquip === idChecklistMaquinaEquip));
      } else {
        setSelectedChecklistMaquinaEquip(undefined);
      }
    }
    setChecklistMaquinaEquipDeleteModal(openState);
  }, [
    checklistMaquinaEquips,
  ]);

  // const handleCopyChecklistServicoModal = useCallback((idChecklistServico: number | null, openState: boolean) => {
  //   if (openState) {
  //     if (idChecklistServico && checklistServicos) {
  //       setSelectedChecklistServico(checklistServicos.find(x => x.idChecklistServico === idChecklistServico));
  //     } else {
  //       setSelectedChecklistServico(undefined);
  //     }
  //   }
  //   setCopyChecklistServicoModal(openState);
  // }, [
  //   checklistServicos,
  // ]);

  const handleEnterChecklist = useCallback((idChecklistMaquinaEquip: number | null, openNewTab: boolean) => {
    if (openNewTab) {
      window.open(`/ChecklistMaquinaEquipItens_cadastro?id=${idChecklistMaquinaEquip}`, '_blank', 'noopener,noreferrer');
    } else {
      backOrNavigate(`/ChecklistMaquinaEquipItens_cadastro?id=${idChecklistMaquinaEquip}`, { alternativeRoute: `/ChecklistMaquinaEquip_lista` });
    }
  }, [
    backOrNavigate,
  ]);

  // const handleEnterVisualize = useCallback((idChecklistServico: number | null, openNewTab: boolean) => {
  //   if (openNewTab) {
  //     window.open(`/ChecklistServico_obrasItem?id=${idChecklistServico}`, '_blank', 'noopener,noreferrer');
  //   } else {
  //     backOrNavigate(`/ChecklistServico_obrasItem?id=${idChecklistServico}`, { alternativeRoute: `/ChecklistServico_lista` });
  //   }
  // }, [
  //   backOrNavigate,
  // ]);


  return {
    loading,
    saving,
    errorMessage,
    checklistMaquinaEquips: checklistMaquinaEquips ?? [],
    selectedChecklistMaquinaEquip,
    checklistMaquinaEquipModal,
    checklistMaquinaEquipDeleteModal,
    copyChecklistMaquinaEquipModal,
    filters,
    pagination,
    reportViewerState,
    handleReload,
    handleChecklistMaquinaEquipSubmit,
    handleChecklistMaquinaEquipDeleteSubmit,
    // handleCopyChecklistServicoSubmit,
    handleCodigoChange,
    handleDescricaoChange,
    handleStatusChange,
    handleClearFilters,
    handlePageChange,
    handleRowsPerPageChange,
    handleChecklistMaquinaEquipModal,
    handleChecklistMaquinaEquipDeleteModal,
    handleEnterChecklist,
  };
}